import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Paper
} from "@mui/material";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { MeasurementConfirmModalProps } from "../interfaces/modals";

export type { MeasurementConfirmModalProps };

const MeasurementConfirmModal: React.FC<MeasurementConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  productName,
  remainingQuantity,
  unit,
  measureType = "Measurement",
  isLoading = false
}) => {
  const { t } = useKivunovaTranslation();
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open) {
      setValue("");
      setError("");
    }
  }, [open]);

  const unitLower = (unit || "").toLowerCase();
  const suggestions =
    unitLower === "kg"
      ? [0.25, 0.5, 1]
      : unitLower === "g"
        ? [100, 250, 500]
        : unitLower === "cm"
          ? [10, 25, 50]
          : unitLower === "m"
            ? [0.5, 1, 2]
            : [0.5, 1, 2];

  const handleConfirm = () => {
    const num = parseFloat(value);
    if (Number.isNaN(num) || num <= 0) {
      setError(
        t("measurement-confirm-enter-valid", "Enter a valid {{measureType}}", {
          measureType: measureType.toLowerCase()
        })
      );
      return;
    }
    if (num > remainingQuantity) {
      setError(
        t(
          "measurement-confirm-max-available",
          "Maximum {{remainingQuantity}} {{unit}} available",
          {
            remainingQuantity,
            unit: unit ?? ""
          }
        )
      );
      return;
    }
    setError("");
    onConfirm(num);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2
        }}
      >
        <Paper
          sx={{
            p: 3,
            maxWidth: 400,
            width: "100%",
            outline: "none"
          }}
          onKeyDown={handleKeyDown}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {productName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("measurement-confirm-remaining", "Remaining")}:{" "}
            {remainingQuantity} {unit}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t(
              "measurement-confirm-enter-label",
              "Enter {{measureType}} ({{unit}})",
              {
                measureType: measureType.toLowerCase(),
                unit: unit ?? ""
              }
            )}
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{ min: 0, max: remainingQuantity, step: "any" }}
            error={!!error}
            helperText={error}
            sx={{ mb: 3 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              "measurement-confirm-suggested-amounts",
              "Suggested {{measureType}} amounts",
              { measureType: measureType.toLowerCase() }
            )}
            :
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            {suggestions.map((s) => (
              <Button
                key={String(s)}
                size="small"
                variant="outlined"
                onClick={() => setValue(String(s))}
              >
                {s} {unit}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={onClose} disabled={isLoading}>
              {t("measurement-confirm-cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isLoading}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {isLoading
                ? t("measurement-confirm-confirming", "Confirming…")
                : t("measurement-confirm-confirm", "Confirm")}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default MeasurementConfirmModal;
